// The five sections (decisions doc › Navigation), in bar order. Shelves is home.
// Each section's feature flag is the one already in the database (migration 0015).

export interface Section {
  key: string;
  name: string;
  path: string;
  flag: string;
  /** The one question it answers (design principle 1). */
  question: string;
  /** Its description line, in the tab-description style, shown while the section has no screens yet
   *  (Discover and Insights as Kristi worded them, Nov 1; the built sections reuse their question). */
  description: string;
}

export const SECTIONS: Section[] = [
  { key: 'shelves', name: 'Shelves', path: '/shelves', flag: 'shelves', question: 'What’s going on, and what do I pick up?', description: 'What’s going on, and what do I pick up?' },
  { key: 'queue', name: 'Queue', path: '/queue', flag: 'queue', question: 'What’s next, in what order?', description: 'What’s next, in what order?' },
  { key: 'discover', name: 'Discover', path: '/discover', flag: 'discover', question: 'What’s new to me?', description: 'What’s new to you: lists, authors and books to add to your list.' },
  { key: 'my-books', name: 'My books', path: '/my-books', flag: 'my_books', question: 'Where are my copies, holds and loans?', description: 'Where are my copies, holds and loans?' },
  { key: 'insights', name: 'Insights', path: '/insights', flag: 'insights', question: 'What have I read, and how is my reading changing?', description: 'What you’ve read, how your reading is changing, and who you want to be as a reader.' },
];

export const HOME: Section = SECTIONS[0]!;

/** The section an address belongs to; shared pages (book…) belong to none. */
export function sectionFor(path: string): Section | undefined {
  if (path === '/' || path === '') return HOME;
  return SECTIONS.find((s) => path === s.path || path.startsWith(s.path + '/'));
}

/** The book page's address: /book/<B-number>. */
export function bookIdFrom(path: string): string | undefined {
  const m = path.match(/^\/book\/([A-Za-z0-9-]{1,40})\/?$/);
  return m?.[1];
}

/** "← Back to Shelves": the section a shared page was opened from (home if unknown). */
export function backSection(from: string | undefined): Section {
  return (from && sectionFor(from)) || HOME;
}

/** An author's or a series' page (Discover, Part 2): /author/<id>, /series/<id>. Shared, like the book page. */
export function peoplePageFrom(path: string): { kind: 'author' | 'series'; id: string } | undefined {
  const m = path.match(/^\/(author|series)\/([0-9a-fA-F-]{36})\/?$/);
  return m ? { kind: m[1] as 'author' | 'series', id: m[2]!.toLowerCase() } : undefined;
}

/**
 * The section a trail of shared pages began in (a book, then its author, then a series…): the
 * section bar keeps it highlighted (Kristi, Nov 3), and it's where a page goes back to when opened
 * directly. `state.section` carries it along the trail.
 */
export function trailSection(state: { from?: string; section?: string }): Section {
  return backSection(state.section ?? state.from);
}

/** The state for a link from a shared page to another: back to here, by name, in the same section. */
export function trailState(path: string, state: { from?: string; section?: string }, name?: string): { from: string; fromName?: string; section: string } {
  // From a section itself, ← Back names the section; from a shared page, the page.
  const section = sectionFor(path) ? (sectionFor(path) as Section) : trailSection(state);
  return { from: path, ...(name && !sectionFor(path) ? { fromName: name } : {}), section: section.path };
}
